import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type IconButtonSize = 'sm' | 'md';
export type IconButtonVariant = 'default' | 'toolbar';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  /** Active state — e.g. a toolbar button reflecting the current mark/node. */
  isActive?: boolean;
  /**
   * Accessible label. Required — IconButtons are icon-only unless `children`
   * is passed, so the label carries the meaning.
   */
  'aria-label': string;
  /** Icon or short text content (e.g. "B" for bold). */
  children: ReactNode;
}

const sizeStyles: Record<IconButtonSize, string> = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-sm',
};

const variantStyles: Record<IconButtonVariant, { idle: string; active: string }> = {
  default: {
    idle: 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100',
    active: 'bg-primary text-white',
  },
  toolbar: {
    idle: 'text-neutral-600 hover:bg-neutral-100',
    active: 'bg-primary text-white',
  },
};

const baseStyles =
  'inline-flex items-center justify-center font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(props, ref) {
    const {
      size = 'md',
      variant = 'default',
      isActive = false,
      className = '',
      type = 'button',
      children,
      ...rest
    } = props;

    const variantCls = isActive ? variantStyles[variant].active : variantStyles[variant].idle;
    const combinedClassName = cn(baseStyles, variantCls, sizeStyles[size], className);

    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={isActive || undefined}
        className={combinedClassName}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
